import { useState } from 'react';
import { Flex, Stack, Text, VStack } from '@chakra-ui/layout';
import { Icon, Input, InputGroup, InputLeftElement, Switch, useToast } from '@chakra-ui/react';
import { Search } from 'lucide-react';
import {
  PERMISSIONS as adminPermissions,
  PERMISSION_IDS as adminPermissionIds,
  POLICIES as adminPolicies,
} from '@duke-hq/svc-iam-policy/admin/permissions';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { User } from '@duke-hq/svc-api-db';
import { useAuthAdmin } from '../../AuthProviderAdmin';
import { iconSize } from '@duke-hq/ui/constants/scalable';

const isPolicyKey = (key: string): key is keyof typeof adminPolicies => {
  return key in adminPolicies;
};

export function AdminUserPermissionsTab({ user }: { user: Omit<User, 'torgId'> }) {
  const [searchQuery, setSearchQuery] = useState('');
  const toast = useToast();
  const session = useAuthAdmin();
  const { permissions: userPermissions, policies: userPolicies, id: userId } = user;

  const policyGrantedPermissions = new Set<keyof typeof adminPermissions>(
    userPolicies
      .filter(isPolicyKey)
      .flatMap((policyId) => [...adminPolicies[policyId].permissions.allow]),
  );
  const allPermissions = adminPermissionIds.filter((id) =>
    adminPermissions[id].label.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const trpcUtils = trpcAdmin.useUtils();
  const updatePermission = trpcAdmin.users.updatePermission.useMutation({
    onSuccess: () => {
      toast({
        title: t`Permission updated successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: error.message,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    },
  });

  return (
    <VStack p={4} gap={4}>
      <InputGroup alignItems="center">
        <InputLeftElement h="full">
          <Icon as={Search} color="fg.muted" boxSize={iconSize.lg} />
        </InputLeftElement>
        <Input
          size="sm"
          variant="outline"
          placeholder={t`Find permissions`}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </InputGroup>
      {allPermissions?.length ? (
        <Stack borderWidth="1px" borderColor="border.emphasis" w="100%" rounded="md" gap={0}>
          {allPermissions.map((permissionId) => {
            const permission = adminPermissions[permissionId];
            const grantedByPolicy = policyGrantedPermissions.has(permissionId);
            const isOwnCanManagePermissions =
              permissionId === 'canManagePermissions' && userId === session.user?.id;

            return (
              <Flex
                key={permissionId}
                _notLast={{
                  borderBottomWidth: 1,
                  borderBottomColor: 'border.emphasis',
                }}
                px={4}
                py={3}
                justifyContent="space-between"
                alignItems="center"
              >
                <Flex direction="column">
                  <Text fontWeight="medium">{permission.label}</Text>
                  {grantedByPolicy ? (
                    <Text fontSize="xs" color="fg.muted">
                      Granted by policy
                    </Text>
                  ) : null}
                </Flex>
                <Switch
                  colorScheme="brand"
                  isChecked={grantedByPolicy || userPermissions.includes(permissionId)}
                  isDisabled={grantedByPolicy || isOwnCanManagePermissions}
                  onChange={async () => {
                    await updatePermission.mutateAsync({
                      userId,
                      permissionId,
                      enabled: !userPermissions.includes(permissionId),
                    });
                    await trpcUtils.users.byId.invalidate(userId);
                    if (userId === session.user?.id) {
                      await trpcUtils.me.invalidate();
                    }
                  }}
                />
              </Flex>
            );
          })}
        </Stack>
      ) : (
        <Text>
          <Trans>No permissions found</Trans>
        </Text>
      )}
    </VStack>
  );
}
